import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Clock, Plus, X, Save, RotateCcw, Loader2, Sparkles } from 'lucide-react';
import { api } from '../lib/api';
import { bestHoursOfDay, useBestTimes } from '../lib/bestTimes';
import { cn } from '../lib/cn';

type Slot = { dayOfWeek: number; hour: number; minute: number };
type ServerSlot = { id: string | null; dayOfWeek: number; hour: number; minute: number };

const DAYS: Array<{ dow: number; label: string }> = [
  { dow: 1, label: 'Lunes' },
  { dow: 2, label: 'Martes' },
  { dow: 3, label: 'Miércoles' },
  { dow: 4, label: 'Jueves' },
  { dow: 5, label: 'Viernes' },
  { dow: 6, label: 'Sábado' },
  { dow: 0, label: 'Domingo' },
];

const DEFAULT_SLOTS: Slot[] = [0, 1, 2, 3, 4, 5, 6].flatMap((dow) =>
  [
    { hour: 9, minute: 0 },
    { hour: 15, minute: 0 },
    { hour: 21, minute: 0 },
  ].map((s) => ({ dayOfWeek: dow, ...s })),
);

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

function slotKey(s: Slot): string {
  return `${s.dayOfWeek}-${s.hour}-${s.minute}`;
}

function compareSlots(a: Slot, b: Slot): number {
  return a.hour - b.hour || a.minute - b.minute;
}

export function QueueSlotsEditor({ onSaved }: { onSaved?: () => void } = {}) {
  const queryClient = useQueryClient();
  const slotsQuery = useQuery({
    queryKey: ['queue', 'slots'],
    queryFn: () => api<{ slots: ServerSlot[] }>('/api/queue/slots'),
  });

  const bestTimes = useBestTimes();

  const [slots, setSlots] = useState<Slot[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [savedMsg, setSavedMsg] = useState<string | null>(null);

  useEffect(() => {
    if (slotsQuery.data) {
      setSlots(
        slotsQuery.data.slots.map((s) => ({
          dayOfWeek: s.dayOfWeek,
          hour: s.hour,
          minute: s.minute,
        })),
      );
    }
  }, [slotsQuery.data]);

  const slotsByDow = useMemo(() => {
    const m = new Map<number, Slot[]>();
    for (const s of slots) {
      const arr = m.get(s.dayOfWeek) ?? [];
      arr.push(s);
      m.set(s.dayOfWeek, arr);
    }
    for (const arr of m.values()) arr.sort(compareSlots);
    return m;
  }, [slots]);

  const saveMutation = useMutation({
    mutationFn: (next: Slot[]) =>
      api<{ slots: ServerSlot[] }>('/api/queue/slots', {
        method: 'PUT',
        body: JSON.stringify({ slots: next }),
      }),
    onSuccess: () => {
      setSavedMsg('Cambios guardados.');
      queryClient.invalidateQueries({ queryKey: ['queue', 'slots'] });
      queryClient.invalidateQueries({ queryKey: ['queue', 'next'] });
      setTimeout(() => setSavedMsg(null), 2500);
      onSaved?.();
    },
    onError: () => {
      setError('No se pudieron guardar los cambios. Inténtalo de nuevo.');
    },
  });

  function addSlot(dow: number, value: string) {
    // value is "HH:MM"
    const match = /^(\d{2}):(\d{2})$/.exec(value);
    if (!match) return;
    const hour = Number(match[1]);
    const minute = Number(match[2]);
    if (Number.isNaN(hour) || Number.isNaN(minute)) return;
    const candidate: Slot = { dayOfWeek: dow, hour, minute };
    const key = slotKey(candidate);
    if (slots.some((s) => slotKey(s) === key)) return;
    setSlots((prev) => [...prev, candidate]);
  }

  function removeSlot(slot: Slot) {
    const key = slotKey(slot);
    setSlots((prev) => prev.filter((s) => slotKey(s) !== key));
  }

  function resetDefaults() {
    setSlots(DEFAULT_SLOTS);
  }

  /** Keep how many slots each day has, but move them to that day's best
   *  hours. Days left empty stay empty; with no slots at all, 2 a day. */
  function applyBestHours() {
    const bt = bestTimes.data;
    if (!bt) return;
    const anySlots = slots.length > 0;
    const next = DAYS.flatMap(({ dow }) => {
      const perDay = anySlots ? (slotsByDow.get(dow)?.length ?? 0) : 2;
      return bestHoursOfDay(bt, dow, perDay).map((hour) => ({ dayOfWeek: dow, hour, minute: 0 }));
    });
    setSlots(next);
    setError(null);
    setSavedMsg(
      bt.source === 'general'
        ? 'Hemos puesto las horas que suelen funcionar en tus redes. Revisa y pulsa Guardar.'
        : 'Hemos puesto las horas en las que mejor te funciona publicar. Revisa y pulsa Guardar.',
    );
  }

  function handleSave() {
    setError(null);
    saveMutation.mutate(slots);
  }

  if (slotsQuery.isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-4">
        <Loader2 size={14} className="animate-spin" /> Cargando horarios…
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </div>
      )}
      {savedMsg && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">
          {savedMsg}
        </div>
      )}

      <div className="space-y-2">
        {DAYS.map((d) => {
          const list = slotsByDow.get(d.dow) ?? [];
          return (
            <DayRow
              key={d.dow}
              label={d.label}
              dow={d.dow}
              slots={list}
              onAdd={(v) => addSlot(d.dow, v)}
              onRemove={removeSlot}
            />
          );
        })}
      </div>

      <div className="flex items-center justify-between gap-3 pt-4 border-t border-slate-100 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={applyBestHours}
            disabled={!bestTimes.data}
            title="Cambia tus horarios por las horas en las que más interacción consiguen tus publicaciones."
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-brand-200 text-brand-700 bg-brand-50 hover:border-brand-300 hover:bg-brand-100 transition disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <Sparkles size={14} /> Usar mis mejores horas
          </button>
          <button
            type="button"
            onClick={resetDefaults}
            className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg border border-slate-200 text-slate-700 hover:border-slate-300 hover:bg-slate-50 transition"
          >
            <RotateCcw size={14} /> Restablecer por defecto
          </button>
        </div>
        <button
          type="button"
          onClick={handleSave}
          disabled={saveMutation.isPending}
          className="inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-lg bg-brand-600 text-white font-medium hover:bg-brand-700 transition disabled:opacity-60 disabled:cursor-not-allowed"
        >
          {saveMutation.isPending ? (
            <Loader2 size={14} className="animate-spin" />
          ) : (
            <Save size={14} />
          )}
          {saveMutation.isPending ? 'Guardando…' : 'Guardar'}
        </button>
      </div>
    </div>
  );
}

function DayRow(props: {
  label: string;
  dow: number;
  slots: Slot[];
  onAdd: (value: string) => void;
  onRemove: (slot: Slot) => void;
}) {
  const { label, slots, onAdd, onRemove } = props;
  const [adding, setAdding] = useState(false);
  const [time, setTime] = useState('12:00');

  function confirm() {
    onAdd(time);
    setAdding(false);
  }

  return (
    <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 flex items-start gap-4 flex-wrap">
      <div className="w-24 shrink-0">
        <span className="text-sm font-medium text-slate-700">{label}</span>
      </div>
      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-1.5">
        {slots.length === 0 && !adding && (
          <span className="text-xs text-slate-400">Sin horarios este día.</span>
        )}
        {slots.map((s) => (
          <span
            key={`${s.hour}-${s.minute}`}
            className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-xs font-medium pl-2.5 pr-1 py-1"
          >
            <Clock size={11} className="text-slate-500" />
            {pad(s.hour)}:{pad(s.minute)}
            <button
              type="button"
              onClick={() => onRemove(s)}
              className="ml-0.5 inline-flex h-4 w-4 items-center justify-center rounded-full hover:bg-slate-200 text-slate-500 hover:text-red-600 transition"
              aria-label={`Quitar horario ${pad(s.hour)}:${pad(s.minute)}`}
            >
              <X size={10} />
            </button>
          </span>
        ))}

        {adding ? (
          <span className="inline-flex items-center gap-1.5">
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              className="text-xs px-2 py-1 rounded-md border border-slate-200 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
            />
            <button
              type="button"
              onClick={confirm}
              className="text-xs px-2 py-1 rounded-md bg-brand-600 text-white font-medium hover:bg-brand-700 transition"
            >
              Añadir
            </button>
            <button
              type="button"
              onClick={() => setAdding(false)}
              className="text-xs px-2 py-1 rounded-md border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Cancelar
            </button>
          </span>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className={cn(
              'inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-dashed border-slate-300 text-slate-500 hover:border-brand-300 hover:text-brand-600 transition',
            )}
          >
            <Plus size={11} /> Añadir hora
          </button>
        )}
      </div>
    </div>
  );
}
